import { Spacing } from '@persona-id/design-tokens';
import { debounce } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from 'design-system/components/Button';
import { Input } from 'design-system/components/Input';
import { HStack } from 'design-system/components/Stack';
import { PaginationParams } from 'lib/interfaces/api';

// After a certain limit, it no longer makes sense for someone
// to jump to the last page. Deep paging has performance limitations.
export const PAGE_LIMIT_BEFORE_SCROLL_MODE = 100;

const PageSelectorStack = styled(HStack).attrs({ justify: 'flex-end' })`
  padding: ${Spacing[2]};
`;

export const PageInput = styled(HStack).attrs({ align: 'center' })`
  white-space: nowrap;
`;

interface PageSelectorProps {
  paginationParams: PaginationParams;
  handlePageLinkClick: (pageNumber: number) => void;
  isLoading?: boolean;
}

export default function PageSelector({
  paginationParams,
  handlePageLinkClick,
  isLoading,
}: PageSelectorProps) {
  const {
    'current-page': currentPage,
    'total-pages': totalPages,
    'lower-bound': lowerBound,
  } = paginationParams ?? {};
  const [selectedPage, setSelectedPage] = useState<number | undefined>(currentPage);

  // We don't use useDebounced to avoid function object reference issues.
  const handlePageLinkClickDebounced = useMemo(
    () => debounce(500, handlePageLinkClick),
    [handlePageLinkClick],
  );

  // Keep input state synced to pagination state when other user action changes
  // pagination state.
  useEffect(() => {
    if (currentPage != null) setSelectedPage(currentPage);
  }, [currentPage]);

  if (!paginationParams) {
    return <div />;
  }

  return (
    <PageSelectorStack data-test='pagination__page-selector' id='pagination__page-selector'>
      <PageInput>
        {totalPages < PAGE_LIMIT_BEFORE_SCROLL_MODE && !lowerBound ? (
          <Input
            aria-label='Go to page number'
            size='sm'
            defaultValue={selectedPage}
            inputSize={2}
            onChange={(e) => {
              const value = e.target.valueAsNumber;
              if (Number.isNaN(value)) {
                setSelectedPage(undefined);
              } else {
                // Setting unbounded value feels smoother as the value is not corrected until after the fetch via useEffect.
                setSelectedPage(value);
                const boundedValue = Math.min(Math.max(e.target.valueAsNumber, 1), totalPages);
                handlePageLinkClickDebounced(boundedValue);
              }
            }}
            onBlur={(e) => {
              // Fill blank value on blur.
              if (Number.isNaN(e.target.valueAsNumber)) setSelectedPage(currentPage);
            }}
            min={1}
            max={totalPages}
            type='number'
            disabled={isLoading}
            value={selectedPage}
          />
        ) : (
          currentPage + ' '
        )}
        of {lowerBound ? 'many' : totalPages} pages
      </PageInput>
      <Button
        id='pagination__page-selector--left'
        disabled={currentPage === 1 || isLoading}
        onClick={() => {
          if (currentPage !== 1) {
            const prevPageNum = currentPage - 1;
            setSelectedPage(prevPageNum);
            handlePageLinkClick(prevPageNum);
          }
        }}
        data-test='pagination__page-selector-paddle-left'
        variant='inverse'
        size='sm'
        width='auto'
        aria-label='Previous page'
      >
        Prev
      </Button>
      <Button
        id='pagination__page-selector--right'
        disabled={(currentPage === totalPages && !lowerBound) || isLoading}
        onClick={() => {
          if (currentPage !== totalPages || lowerBound) {
            const nextPageNum = currentPage + 1;
            setSelectedPage(nextPageNum);
            handlePageLinkClick(nextPageNum);
          }
        }}
        data-test='pagination__page-selector-paddle-right'
        variant='inverse'
        size='sm'
        width='auto'
        aria-label='Next page'
      >
        Next
      </Button>
    </PageSelectorStack>
  );
}
